import { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import { AuthProvider } from './hoc/AuthContext';
import { routes } from './Routes';

import './Styles/fonts.css';
import './Styles/CommonScss/style.scss';
import './Styles/CommonScss/responsive.scss';
import './Styles/CommonScss/common.scss';
import './global.css';

const QUERY_STALE_TIME = 5 * 60 * 1000;
const QUERY_GC_TIME = 10 * 60 * 1000;
const PERSISTED_QUERY_MAX_AGE = 10 * 60 * 1000;
const PERSISTED_QUERY_CACHE_BUSTER = 'rq-cache-v2';
const ReactQueryDevtools = process.env.NODE_ENV === 'development'
  ? lazy(() => import('@tanstack/react-query-devtools').then((module) => ({
      default: module.ReactQueryDevtools,
    })))
  : null;

function getPersistStorage() {
  if (typeof window === 'undefined') return null;

  try {
    const { localStorage } = window;
    const testKey = '__bankco_query_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return localStorage;
  } catch {
    return null;
  }
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME,
      gcTime: QUERY_GC_TIME,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

const persistStorage = getPersistStorage();
const persister = persistStorage
  ? createSyncStoragePersister({
      storage: persistStorage,
      key: 'REACT_QUERY_OFFLINE_CACHE',
      throttleTime: 1000,
    })
  : null;

function startQueryCachePersistence() {
  if (!persister || typeof window === 'undefined') return;

  const persistCache = () => {
    const [, restorePromise] = persistQueryClient({
      queryClient,
      persister,
      maxAge: PERSISTED_QUERY_MAX_AGE,
      buster: PERSISTED_QUERY_CACHE_BUSTER,
      dehydrateOptions: {
        shouldDehydrateQuery: (query) => query.state.status === 'success',
      },
    });

    restorePromise.catch((err) => {
      console.error('Query cache persistence failed:', err);
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(persistCache, { timeout: 1500 });
    return;
  }

  window.setTimeout(persistCache, 0);
}

const rootElement = document.getElementById('root');
const app = (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <RouterProvider router={routes} />
      </AuthProvider>
    </HelmetProvider>
    {ReactQueryDevtools && (
      <Suspense fallback={null}>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    )}
  </QueryClientProvider>
);

createRoot(rootElement).render(app);
startQueryCachePersistence();

if (process.env.NODE_ENV === 'development') {
  window.addEventListener('beforeunload', () => {
    queryClient.clear();
  });
}
